import { type MouseEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  type ChatMessage,
  ChatPropertiesType,
  type ProductInfo,
  SenderRole,
  Status,
  SupportType,
} from "@b2bportal/chat-api";
import {
  ChatInternalContextProvider,
  type IChatProvider,
} from "@b2bportal/core-chat";
import {
  endChatSession,
  getSupportProductInfo,
} from "@b2bportal/core-self-serve";
import {
  ChatAvatar,
  ChatDivider,
  ChatInputContainer,
  ChatIntroMessage,
  ChatMessageComponent,
  ChatWindowBottomSheet,
  ChatWindowHeader,
  TypingIndicator,
  getBaseTripDetailsPath,
} from "@hopper-b2b/chat";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useChatSupport } from "@hopper-b2b/self-serve";
import { Slot } from "@hopper-b2b/ui";
import { copyToClipboard } from "@hopper-b2b/utilities";
import { Box, Menu, MenuItem } from "@material-ui/core";
import clsx from "clsx";

import messageStyles from "../ChatMessage/ChatMessage.module.scss";
import styles from "./ChatWindow.module.scss";

type ContextMenuAnchor = {
  left: number;
  top: number;
};

export interface IChatWindowProps {
  chatProvider: IChatProvider;
  /**
   * Ends the current chat session and hides the chat window
   */
  closeChat: () => void;
  copyConversationIdEnabled?: boolean;
  /**
   * Hides the chat window but keeps the chat session active
   */
  minimizeChat: () => void;
  mobile?: boolean;
  open: boolean;
  getTripDetailsPath?: (productInfo: ProductInfo) => string;
}

const ChatWindow: React.FC<IChatWindowProps> = ({
  chatProvider,
  closeChat,
  copyConversationIdEnabled = false,
  minimizeChat,
  mobile = false,
  open,
  getTripDetailsPath = getBaseTripDetailsPath,
}) => {
  const { t } = useI18nContext();
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>();
  const [contextMenu, setContextMenu] = useState<ContextMenuAnchor>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [conversationIsLoaded, setConversationIsLoaded] = useState(false);
  const [conversationIsLoading, setConversationIsLoading] = useState(false);
  const supportProductInfo = useSelector(getSupportProductInfo);
  const dispatch = useDispatch();

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const showPendingMessage = (text: string) => {
    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: String(currentMessages.length + 1),
        senderRole: SenderRole.User,
        content: text,
        timestamp: new Date().toString(),
        deepLink: null,
        smartReplies: null,
        status: Status.Pending,
      },
    ]);
  };

  /**
   * Updates the status of the user's last message and adds the agent's reply to
   * the message list (if there was one)
   */
  const handleAgentReply = (
    userMessageStatus: Status,
    agentReply?: ChatMessage
  ) => {
    setMessages((currentMessages) => {
      // replace Array.prototype.findLastIndex once supported
      let lastUserMsgIdx = currentMessages.length - 1;
      for (; lastUserMsgIdx >= 0; lastUserMsgIdx -= 1) {
        const { senderRole } = currentMessages[lastUserMsgIdx];

        if (senderRole === SenderRole.User) break;
      }

      if (lastUserMsgIdx !== -1) {
        currentMessages[lastUserMsgIdx].status = userMessageStatus;
      }

      if (agentReply) {
        return [...currentMessages, agentReply];
      }

      return currentMessages;
    });
  };

  const openContextMenu = (ev: MouseEvent) => {
    const { clientX, clientY } = ev;

    ev.preventDefault();

    setContextMenu((currentContextMenu) => {
      if (currentContextMenu == null) {
        return {
          left: clientX,
          top: clientY,
        };
      }

      return null;
    });
  };

  const onSendMessage = async (text: string) => {
    setIsAgentTyping(true);
    showPendingMessage(text);

    try {
      const reply = await chatProvider.sendMessage(conversationId, text);
      handleAgentReply(Status.Sent, reply);
    } catch (error) {
      console.error("An error occurred while sending the message:", error);
      handleAgentReply(Status.Failed);

      // bubble up error to ChatInputContainer to handle error
      throw error;
    } finally {
      setIsAgentTyping(false);
    }
  };

  useEffect(() => {
    const onCreateConversation = async () => {
      setConversationIsLoading(true);
      setIsAgentTyping(true);

      const reply = await chatProvider.createConversation(
        supportProductInfo
          ? {
              itineraryUuid: supportProductInfo.productId,
              travelCategory: supportProductInfo.productType,
            }
          : undefined
      );

      setConversationId(reply.id);
      setConversationIsLoaded(true);
      setConversationIsLoading(false);
      setIsAgentTyping(false);
      setMessages([reply.messages[0]]);
    };

    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

    if (!conversationIsLoaded && !conversationIsLoading) {
      onCreateConversation();
    }
  }, [
    chatProvider,
    conversationIsLoaded,
    conversationIsLoading,
    supportProductInfo,
  ]);

  useEffect(() => {
    if (open) {
      chatInputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      // deconstructor to end chat sessions if chat is active
      // when component unmounts
      if (conversationId) {
        chatProvider.closeConversation(conversationId);
        setConversationId(null);
      }
    };
  }, [chatProvider, conversationId]);

  return (
    <ChatInternalContextProvider
      chatContext={{
        chatProvider,
        conversationId,
        setConversationId,
        closeChatWindow: closeChat,
        minimizeChatWindow: minimizeChat,
        getTripDetailsPath: getTripDetailsPath,
      }}
    >
      <div
        className={clsx(
          "chat-window",
          {
            [styles.mobileChatWindow]: mobile,
            [styles.open]: open,
          },
          styles.chatWindow
        )}
        ref={chatWindowRef}
      >
        <Slot
          id="chat-window-header"
          component={<ChatWindowHeader onMinimize={minimizeChat} />}
        />
        <div
          className={clsx("message-list", styles.messageList)}
          onContextMenu={
            copyConversationIdEnabled ? openContextMenu : undefined
          }
        >
          {/* TODO incorporate https://day.js.org/docs/en/display/calendar-time if needed */}
          <ChatDivider value={t("chat.chatTodayDivider")} />
          <ChatIntroMessage />
          {messages.map((message) => (
            <ChatMessageComponent
              showMetadata
              key={message.id}
              message={message}
            />
          ))}
          {isAgentTyping && (
            // TODO replace with ChatMessageComponent
            <div className={clsx(messageStyles.messageContainer)}>
              <ChatAvatar className={messageStyles.messageAvatar} />
              <div className={clsx(messageStyles.messageContent)}>
                <TypingIndicator />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
          {copyConversationIdEnabled && (
            <Menu
              anchorPosition={contextMenu}
              anchorReference="anchorPosition"
              onClose={closeContextMenu}
              open={contextMenu !== null}
            >
              <MenuItem
                onClick={() => {
                  copyToClipboard(conversationId);
                  closeContextMenu();
                }}
              >
                {t("chat.copyConversationId")}
              </MenuItem>
            </Menu>
          )}
        </div>
        <ChatInputContainer
          conversationId={conversationId}
          inputRef={chatInputRef}
          sendMessage={onSendMessage}
        />
        <ChatWindowBottomSheet chatWindowRef={chatWindowRef} />
      </div>
    </ChatInternalContextProvider>
  );
};

export interface IChatWindowContainerProps extends IChatWindowProps {
  containerClassName?: string;
}

export const ChatWindowContainer: React.FC<IChatWindowContainerProps> = (
  props
) => {
  return (
    <Box
      className={clsx(
        styles.floatingBox,
        { [styles.mobile]: props.mobile, [styles.open]: props.open },
        props.containerClassName
      )}
    >
      <ChatWindow {...props} />
    </Box>
  );
};

export default ChatWindow;
